"use client";

import { Users, Sparkles, TrendingUp, Target, Flame, Heart } from "lucide-react";

export type SocialProofType =
  | "similar-traders"    // "Traders like you traded this"
  | "popular"           // "Popular with similar traders"
  | "matches-interests" // "Matches your interests"
  | "trending"          // "Trending now"
  | "hot-debate"        // "Hot debate - close to 50/50"
  | "new"               // "Newly created market"
  | "based-on-trades"   // "Based on your X trades"
  | "custom";           // Custom reason string

interface SocialProofBadgeProps {
  type?: SocialProofType;
  reason?: string;
  category?: string;
  compact?: boolean;
  className?: string;
}

const BADGE_CONFIG: Record<SocialProofType, {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  gradient: string;
  iconColor: string;
}> = {
  "similar-traders": {
    icon: Users,
    label: "Traders like you",
    gradient: "from-blue-500/20 to-purple-500/10",
    iconColor: "text-blue-400",
  },
  "popular": {
    icon: Flame,
    label: "Popular with similar traders",
    gradient: "from-orange-500/20 to-red-500/10",
    iconColor: "text-orange-400",
  },
  "matches-interests": {
    icon: Target,
    label: "Matches your interests",
    gradient: "from-[#CCFF00]/20 to-green-500/10",
    iconColor: "text-[#CCFF00]",
  },
  "trending": {
    icon: TrendingUp,
    label: "Trending now",
    gradient: "from-[#CCFF00]/20 to-transparent",
    iconColor: "text-[#CCFF00]",
  },
  "hot-debate": {
    icon: Flame,
    label: "Hot debate",
    gradient: "from-red-500/20 to-orange-500/10",
    iconColor: "text-red-400",
  },
  "new": {
    icon: Sparkles,
    label: "Newly created",
    gradient: "from-purple-500/20 to-pink-500/10",
    iconColor: "text-purple-400",
  },
  "based-on-trades": {
    icon: Heart,
    label: "Based on your trades",
    gradient: "from-pink-500/20 to-red-500/10",
    iconColor: "text-pink-400",
  },
  "custom": {
    icon: Sparkles,
    label: "",
    gradient: "from-[#CCFF00]/20 to-transparent",
    iconColor: "text-[#CCFF00]",
  },
};

// Map backend reason strings to badge types
function mapReasonToType(reason: string): SocialProofType {
  const reasonLower = reason.toLowerCase();

  if (reasonLower.includes("traders like you") || reasonLower.includes("similar traders")) {
    return "similar-traders";
  }
  if (reasonLower.includes("popular with similar")) {
    return "popular";
  }
  if (reasonLower.includes("matches your interests") || reasonLower.includes("similar to markets you like")) {
    return "matches-interests";
  }
  if (reasonLower.includes("trending")) {
    return "trending";
  }
  if (reasonLower.includes("hot debate") || reasonLower.includes("50/50") || reasonLower.includes("close to 50")) {
    return "hot-debate";
  }
  if (reasonLower.includes("newly created") || reasonLower.includes("new market")) {
    return "new";
  }
  if (reasonLower.includes("based on your") && reasonLower.includes("trades")) {
    return "based-on-trades";
  }

  return "custom";
}

export default function SocialProofBadge({
  type,
  reason,
  category,
  compact = false,
  className = "",
}: SocialProofBadgeProps) {
  // Determine the type from reason if not provided
  const effectiveType = type || (reason ? mapReasonToType(reason) : "trending");
  const config = BADGE_CONFIG[effectiveType];

  // For custom type, use the reason as label
  const displayLabel = effectiveType === "custom" && reason
    ? reason
    : effectiveType === "based-on-trades" && category
      ? `Based on your ${category} trades`
      : config.label;

  const Icon = config.icon;

  if (compact) {
    return (
      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-[5px] bg-gradient-to-r ${config.gradient} border border-white/5 ${className}`}
      >
        <Icon size={10} className={config.iconColor} />
        <span className="text-[9px] font-medium text-gray-300 truncate max-w-[100px]">
          {displayLabel}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[5px] bg-gradient-to-r ${config.gradient} border border-white/5 backdrop-blur-sm ${className}`}
    >
      <Icon size={12} className={config.iconColor} />
      <span className="text-[11px] font-medium text-gray-200">
        {displayLabel}
      </span>
    </div>
  );
}

// Inline version for card footers
export function SocialProofInline({
  reason,
  className = "",
}: {
  reason: string;
  className?: string;
}) {
  // "Trending" is redundant info — the user is already on a trending list,
  // so hide the label entirely. Keeps other reasons visible.
  const reasonLower = (reason || '').toLowerCase();
  if (reasonLower === 'trending' || reasonLower === 'trending now' || reasonLower.startsWith('trending ')) {
    return null;
  }
  // "Newly created" is redundant on the New tab — hide entirely.
  if (reasonLower.includes('newly created')) {
    return null;
  }

  const type = mapReasonToType(reason);
  const config = BADGE_CONFIG[type];
  const Icon = config.icon;

  // Short display label for inline use
  const getShortLabel = (r: string) => {
    const reasonLower = r.toLowerCase();
    if (reasonLower.includes("traders like you")) return "Similar traders";
    if (reasonLower.includes("popular with similar")) return "Popular";
    if (reasonLower.includes("matches your interests")) return "For you";
    if (reasonLower.includes("similar to markets")) return "Similar";
    if (reasonLower.includes("trending")) return "Trending";
    if (reasonLower.includes("hot debate") || reasonLower.includes("50/50")) return "Hot debate";
    if (reasonLower.includes("based on your")) {
      // Extract category: "Based on your Politics trades" -> "Politics"
      const match = r.match(/Based on your (\w+) trades/i);
      return match ? match[1] : "For you";
    }
    // Truncate long custom reasons
    return r.length > 15 ? r.substring(0, 12) + "..." : r;
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Icon size={11} className={config.iconColor} />
      <span className="text-[10px] font-medium text-gray-400">
        {getShortLabel(reason)}
      </span>
    </div>
  );
}
